import { useRef } from 'react';

/** The slice of a React pointer/mouse event the backdrop rule reads. */
interface BackdropEventLike {
  target: unknown;
  currentTarget: unknown;
  /** Pointer button; 0 is the primary button. Absent on a click event. */
  button?: number;
}

export interface BackdropDismissProps {
  onPointerDownCapture: (e: BackdropEventLike) => void;
  onPointerUpCapture: (e: BackdropEventLike) => void;
  onClick: (e?: BackdropEventLike) => void;
}

/**
 * Pure core of the backdrop-dismiss rule, kept separate from the hook so it
 * can be tested under plain node (no DOM, no effects).
 *
 * Why it keys off where the PRESS started: when a mousedown and mouseup land on
 * different elements — e.g. a drag-select of text inside a card that is released
 * over the dimmed backdrop — the browser fires `click` on their nearest common
 * ancestor, which is the backdrop itself. A plain `onClick={onClose}` on the
 * backdrop (with the card merely calling `stopPropagation`) therefore closed the
 * window mid-selection. `onPointerDownCapture` records whether the press began on the
 * backdrop itself (`target === currentTarget`); `onClick` dismisses only when
 * it did, then always resets. A click with no preceding pointerdown (synthetic
 * or keyboard-driven) never dismisses.
 *
 * Both ends must be on the backdrop: a press on the backdrop that is dragged
 * INTO the card and released there also produces a click on the common
 * ancestor (the backdrop), so the release (`pointerup`) is checked too. The
 * press and release are recorded in the CAPTURE phase so an inner element that
 * stops pointer-event propagation can never skip the reset, and only a primary
 * button press arms the rule; any other press disarms it.
 *
 * `readDismiss` is called at click time so the latest callback runs, never a
 * stale one.
 */
export function createBackdropDismiss(readDismiss: () => () => void): BackdropDismissProps {
  let pressStartedOnBackdrop = false;
  let releasedOnBackdrop = false;
  return {
    onPointerDownCapture: (e) => {
      pressStartedOnBackdrop = e.target === e.currentTarget && (e.button ?? 0) === 0;
      releasedOnBackdrop = false;
    },
    onPointerUpCapture: (e) => {
      releasedOnBackdrop = e.target === e.currentTarget;
    },
    onClick: () => {
      const shouldDismiss = pressStartedOnBackdrop && releasedOnBackdrop;
      pressStartedOnBackdrop = false;
      releasedOnBackdrop = false;
      if (shouldDismiss) readDismiss()();
    },
  };
}

/**
 * Props for a hand-rolled full-viewport backdrop whose own element dismisses on
 * click. Spread onto the backdrop element. Not for Radix dialogs (they dismiss
 * on pointerdown-outside already). See `createBackdropDismiss` for the why.
 */
export function useBackdropDismiss(onDismiss: () => void): BackdropDismissProps {
  const dismissRef = useRef(onDismiss);
  // Read at click time on purpose (via `readDismiss`): some callers' dismiss
  // decision depends on state that changes after mount (operator panel's
  // terminal condition), so a callback captured at first render would be stale.
  dismissRef.current = onDismiss;
  const handlersRef = useRef<BackdropDismissProps | null>(null);
  if (handlersRef.current === null) {
    handlersRef.current = createBackdropDismiss(() => dismissRef.current);
  }
  return handlersRef.current;
}
